import React from "react";
import { useAuth } from "wasp/client/auth";
import { useQuery } from "wasp/client/operations";
import { getAdminEventsSummary } from "wasp/client/operations";
// AppFrame removed: route-level layout wraps admin pages
import { AnimatedSkeleton } from "../../components/ui/animated-skeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";
import { Line, LineChart, CartesianGrid, XAxis, YAxis, Legend } from "recharts";

export function AdminEvents() {
  const { data: user } = useAuth();

  const { data, isLoading } = useQuery(getAdminEventsSummary as any, {
    sinceDays: 30,
  }) as any;
  const series = (data?.series || []).map((d: any) => ({
    ...d,
    active_min: Math.round(((d.active_ms || 0) / 60000) * 10) / 10,
  }));
  const totals = data?.totals || {};
  const pv = Number(totals.pricing_view || 0);
  const trial = Number(totals.trial_start_click || 0);
  const conv = pv ? (trial / pv) * 100 : 0;
  const activeMinTotal =
    Math.round(((totals.active_ms || 0) / 60000) * 10) / 10;
  const onboardingAvgSec = totals.onboarding_complete
    ? Math.round(totals.onboarding_ms / totals.onboarding_complete / 100) / 10
    : 0;
  const topReferrers = data?.topReferrers || [];
  const topFirst = data?.topFirstReferrers || [];
  const variants = data?.variants || [];

  const chartConfig = {
    pricing_view: { label: "Pricing Views", color: "#FFC562" },
    trial_start_click: { label: "Trial Starts", color: "hsl(var(--sage))" },
    swipe_like: { label: "Likes", color: "#7CB342" },
    swipe_skip: { label: "Skips", color: "#EF6C00" },
    plan_open: { label: "Plan Opens", color: "#42A5F5" },
    active_min: { label: "Active Minutes", color: "#6D28D9" },
  } as const;

  if (isLoading) {
    return (
        <div className="space-y-6">
          <div className="mb-8">
            <AnimatedSkeleton
              variant="shimmer"
              className="h-7 w-64 rounded-md"
            />
            <AnimatedSkeleton
              variant="shimmer"
              className="mt-2 h-4 w-96 rounded"
            />
          </div>
          <AnimatedSkeleton variant="wave" className="h-64 rounded-xl" />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <AnimatedSkeleton
                key={i}
                variant="wave"
                className="h-28 rounded-xl"
              />
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <AnimatedSkeleton
                key={i}
                variant="wave"
                className="h-40 rounded-xl"
              />
            ))}
          </div>
        </div>
    );
  }

  return (
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-martinique dark:text-white">
            Events Analytics
          </h1>
          <p className="mt-2 text-martinique/70 dark:text-white/70">
            User engagement events from the last {data?.sinceDays ?? 30} days
          </p>
        </div>

        <Card className="rounded-xl border-0 bg-sand shadow-card dark:bg-darkcard">
          <CardHeader>
            <CardTitle className="text-xl text-martinique dark:text-white">
              Key Events Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig as any} className="w-full">
              <LineChart
                data={series}
                margin={{ left: 16, right: 16, top: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Legend />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line
                  type="monotone"
                  dataKey="pricing_view"
                  stroke="var(--color-pricing_view)"
                  dot={false}
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="trial_start_click"
                  stroke="var(--color-trial_start_click)"
                  dot={false}
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="swipe_like"
                  stroke="var(--color-swipe_like)"
                  dot={false}
                  strokeWidth={1.5}
                />
                <Line
                  type="monotone"
                  dataKey="swipe_skip"
                  stroke="var(--color-swipe_skip)"
                  dot={false}
                  strokeWidth={1.5}
                />
                <Line
                  type="monotone"
                  dataKey="plan_open"
                  stroke="var(--color-plan_open)"
                  dot={false}
                  strokeWidth={1.5}
                />
                <Line
                  type="monotone"
                  dataKey="active_min"
                  stroke="var(--color-active_min)"
                  dot={false}
                  strokeWidth={1.5}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* KPI Row */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card className="hover:shadow-cardHover rounded-xl border-0 bg-sand shadow-card transition-all duration-200 dark:bg-darkcard">
            <CardContent className="p-6">
              <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
                Pricing → Trial (CTR)
              </div>
              <div className="text-3xl font-bold text-martinique dark:text-white">
                {isFinite(conv) ? `${conv.toFixed(1)}%` : "—"}
              </div>
            </CardContent>
          </Card>
          <Card className="hover:shadow-cardHover rounded-xl border-0 bg-sand shadow-card transition-all duration-200 dark:bg-darkcard">
            <CardContent className="p-6">
              <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
                Trial Starts
              </div>
              <div className="text-3xl font-bold text-martinique dark:text-white">
                {trial}
              </div>
            </CardContent>
          </Card>
          <Card className="hover:shadow-cardHover rounded-xl border-0 bg-sand shadow-card transition-all duration-200 dark:bg-darkcard">
            <CardContent className="p-6">
              <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
                Active Minutes (total)
              </div>
              <div className="text-3xl font-bold text-martinique dark:text-white">
                {activeMinTotal}
              </div>
            </CardContent>
          </Card>
          <Card className="hover:shadow-cardHover rounded-xl border-0 bg-sand shadow-card transition-all duration-200 dark:bg-darkcard">
            <CardContent className="p-6">
              <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
                Onboarding avg (sec)
              </div>
              <div className="text-3xl font-bold text-martinique dark:text-white">
                {onboardingAvgSec}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="rounded-xl border-0 bg-sand shadow-card dark:bg-darkcard">
            <CardHeader>
              <CardTitle className="text-lg text-martinique dark:text-white">
                Top Referrers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {topReferrers.map(([host, count]: any) => (
                  <li
                    key={host}
                    className="flex justify-between border-b border-white/5 pb-1"
                  >
                    <span
                      className="truncate text-martinique dark:text-white"
                      title={host}
                    >
                      {host}
                    </span>
                    <span className="text-martinique/70 dark:text-white/70">
                      {count}
                    </span>
                  </li>
                ))}
                {!topReferrers.length && (
                  <div className="text-martinique/70 dark:text-white/70">—</div>
                )}
              </ul>
            </CardContent>
          </Card>

          <Card className="rounded-xl border-0 bg-sand shadow-card dark:bg-darkcard">
            <CardHeader>
              <CardTitle className="text-lg text-martinique dark:text-white">
                First Referrer (60d)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {topFirst.map(([host, count]: any) => (
                  <li
                    key={host}
                    className="flex justify-between border-b border-white/5 pb-1"
                  >
                    <span
                      className="truncate text-martinique dark:text-white"
                      title={host}
                    >
                      {host}
                    </span>
                    <span className="text-martinique/70 dark:text-white/70">
                      {count}
                    </span>
                  </li>
                ))}
                {!topFirst.length && (
                  <div className="text-martinique/70 dark:text-white/70">—</div>
                )}
              </ul>
            </CardContent>
          </Card>

          <Card className="rounded-xl border-0 bg-sand shadow-card dark:bg-darkcard">
            <CardHeader>
              <CardTitle className="text-lg text-martinique dark:text-white">
                Variant Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {variants.map(([variant, count]: any) => (
                  <li
                    key={variant}
                    className="flex justify-between border-b border-white/5 pb-1"
                  >
                    <span className="text-martinique dark:text-white">
                      {variant}
                    </span>
                    <span className="text-martinique/70 dark:text-white/70">
                      {count}
                    </span>
                  </li>
                ))}
                {!variants.length && (
                  <div className="text-martinique/70 dark:text-white/70">—</div>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
  );
}

export default AdminEvents;
